/**
 * The game over screen.
 *
 * Shows the verdict, the shape of the match, and the two ways out: rematch on a
 * fresh seed, or back to the PLAY hub. It also records the match exactly once,
 * into matchHistory and the profile stats, which is why the recording lives
 * here rather than in the controller: the controller has no business knowing
 * about the player's profile.
 */

import { useEffect, useRef } from 'react';
import type { GameState } from '@/engine';
import { useMatchHistory, useProfile } from '@/app/stores';
import { recordMatchForAchievements } from '@/achievements';
import { Button, Modal } from '@/ui/system';
import type { MatchController, MatchConfig } from './matchController';
import styles from './Battlefield.module.css';

export interface GameOverScreenProps {
  state: GameState;
  config: MatchConfig;
  /** Source of the measured summary. See matchStats.ts. */
  controller: MatchController;
  localPlayer: string;
  onRematch: () => void;
  onExit: () => void;
}

export function GameOverScreen({
  state,
  config,
  controller,
  localPlayer,
  onRematch,
  onExit,
}: GameOverScreenProps) {
  const record = useMatchHistory((s) => s.record);
  const recordResult = useProfile((s) => s.recordResult);
  const recorded = useRef(false);

  const result = state.draw ? 'draw' : state.winner === localPlayer ? 'win' : 'loss';
  const opponent = state.playerOrder.find((id) => id !== localPlayer) ?? localPlayer;

  const damageDealt = Math.max(0, state.options.startingIntegrity - (state.players[opponent]?.integrity ?? 0));
  const damageTaken = Math.max(0, state.options.startingIntegrity - (state.players[localPlayer]?.integrity ?? 0));

  /*
   * Record once. StrictMode double-mounts in development, hence the ref.
   *
   * The summary comes from the controller, which measured it as the match was
   * played; recomputing it here from the final state would silently lose every
   * "at one point during the game" field. `recordMatchForAchievements` is
   * idempotent, and the history store calls it too as a backstop, so calling it
   * here as well is safe and means the badge toast fires on this screen.
   */
  useEffect(() => {
    if (recorded.current) return;
    recorded.current = true;
    const summary = controller.summary();
    record(summary);
    recordResult(summary.result);
    recordMatchForAchievements({
      ...summary,
      id: `pending-${config.seed}`,
      playedAt: Date.now(),
    });
  }, [config.seed, controller, record, recordResult]);

  const verdict = result === 'win' ? 'Victory' : result === 'loss' ? 'Defeat' : 'Draw';
  const verdictClass =
    result === 'win' ? styles.resultWin : result === 'loss' ? styles.resultLoss : styles.resultDraw;

  const reason = state.draw
    ? 'Both nodes went down at once.'
    : result === 'win'
      ? `${state.players[opponent]?.name ?? 'Your opponent'} is out of Integrity.`
      : 'Your Integrity hit zero.';

  return (
    <Modal
      open
      dismissible={false}
      title="Match complete"
      size="medium"
      footer={
        <>
          <Button variant="ghost" onClick={onExit}>
            Back to Play
          </Button>
          <Button variant="primary" onClick={onRematch} data-testid="rematch">
            Rematch
          </Button>
        </>
      }
    >
      <div className={styles.result} data-testid="game-over">
        <div className={`${styles.resultVerdict} ${verdictClass}`}>{verdict}</div>
        <p>{reason}</p>
        <div className={styles.resultStats}>
          <Tile value={state.turn} label="Turns" />
          <Tile value={damageDealt} label="Integrity taken" />
          <Tile value={damageTaken} label="Integrity lost" />
        </div>
      </div>
    </Modal>
  );
}

function Tile({ value, label }: { value: number; label: string }) {
  return (
    <div className={styles.resultTile}>
      <div className={styles.resultTileValue}>{value}</div>
      <div className={styles.resultTileLabel}>{label}</div>
    </div>
  );
}
